import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { workflowSettingsPath } from "@/features/Agents/routes";

import { JobsService } from "../services/jobsService";
import type { JobRunRow } from "../types/job";
import { LIVE_RUN_STATUSES, formatDuration } from "../utils/runs";
import { agentReportOf } from "../utils/runs";
import { JobRunReport } from "./JobRunReport";
import { JobRunStatusBadge } from "./JobRunStatusBadge";

interface JobRunSheetProps {
  productId: string;
  row: JobRunRow | null;
  /** O SSE está entregando as mudanças: a consulta periódica para. */
  realtime: boolean;
  onClose: () => void;
}

/** Uma execução do histórico geral: o relatório completo do agente (task 86869). */
export function JobRunSheet({ productId, row, realtime, onClose }: JobRunSheetProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const runQuery = useQuery({
    queryKey: ["job-run", productId, row?.execution_id],
    queryFn: () => JobsService.run(productId, row!.execution_id),
    enabled: Boolean(row),
    refetchInterval: (query) =>
      !realtime && LIVE_RUN_STATUSES.has(query.state.data?.status ?? row?.status ?? "") ? 5_000 : false,
  });
  const run = runQuery.data;
  const { error } = agentReportOf(run);
  const started = row?.started_at ? new Date(row.started_at).toLocaleString(i18n.language) : "—";

  return (
    <Sheet open={Boolean(row)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        {row && (
          <>
            <SheetHeader>
              <SheetTitle>{row.job.name}</SheetTitle>
              <SheetDescription>
                {started} · {formatDuration(row.duration_ms)}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-4">
              <JobRunStatusBadge status={run?.status ?? row.status} />
              {error && <p className="text-sm text-destructive">{error}</p>}
              {runQuery.isLoading ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("jobs.runs.loadingRun")}
                </p>
              ) : run ? (
                <JobRunReport run={run} detailed />
              ) : (
                <p className="text-sm text-destructive">{t("jobs.runs.runLoadFailed")}</p>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate(workflowSettingsPath(row.job.id))}>
                {t("jobs.runs.openJob")}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
